import { useCallback, useEffect, useMemo, useState } from 'react';
import type { NavModule } from '@biovet/ui';
import { MODULES } from '../nav.js';


export interface Route {
  module: string;
  item: string;
}

export const HOME: Route = { module: 'dashboard', item: 'overview' };

export function pathFor(route: Route): string {
  return `/${route.module}/${route.item}`;
}

export function routeFor(pathname: string): Route {
  const [moduleKey, itemKey] = pathname.split('/').filter(Boolean);
  const owner = MODULES.find((m) => m.key === moduleKey);
  if (!owner) return HOME;

  const item = owner.items.find((i) => i.key === itemKey) ?? owner.items[0];
  return item ? { module: owner.key, item: item.key } : HOME;
}

export function moduleOf(itemKey: string): string | undefined {
  return MODULES.find((m) => m.items.some((i) => i.key === itemKey))?.key;
}

export function withHrefs(modules: NavModule[]): NavModule[] {
  return modules.map((module) => ({
    ...module,
    href: module.items[0] && pathFor({ module: module.key, item: module.items[0].key }),
    items: module.items.map((item) => ({
      ...item,
      href: pathFor({ module: module.key, item: item.key }),
    })),
  }));
}

export function useRoute(): {
  route: Route;
  navigate: (next: Route, options?: { replace?: boolean }) => void;
} {
  const [route, setRoute] = useState<Route>(() => routeFor(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(routeFor(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((next: Route, options?: { replace?: boolean }) => {
    const path = pathFor(next);
    if (path !== window.location.pathname) {
      const url = path + window.location.search;
      if (options?.replace) window.history.replaceState(null, '', url);
      else window.history.pushState(null, '', url);
    }
    setRoute(next);
  }, []);

  useEffect(() => {
    if (pathFor(route) !== window.location.pathname) {
      window.history.replaceState(null, '', pathFor(route) + window.location.search);
    }
  }, [route]);

  return useMemo(() => ({ route, navigate }), [route, navigate]);
}
